import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';

import type {
    AuthCapability,
    EmgAuthClaims,
    EmgAuthClient,
    EmgAuthConfig,
    EmgAuthLoginOptions,
} from './types';

/**
 * EAA-1730: the single OIDC session behavior for every EMG web app. Replaces the
 * near-identical `createEmgAuth` copies that portal (EAA-1756) and web-content
 * (EAA-1762) each grew — one wrapper, one source of truth (design principle 3), so a
 * fix to renew/logout/session-end lands everywhere at once.
 *
 * Key decisions baked in here (not left to callers):
 *   - Authorization Code + PKCE — oidc-client-ts defaults a public client to S256.
 *   - Access token in memory only; refresh token in the default sessionStorage store
 *     (per-tab — a new tab does a top-level redirect, instant when the SSO cookie is
 *     present), NEVER localStorage, so switching sessions in one tab can't bleed into
 *     another.
 *   - Renewal via ROTATED REFRESH TOKENS, not the check-session iframe: third-party
 *     cookie blocking makes silent-renew iframes unreliable, so `monitorSession` is
 *     off and `automaticSilentRenew` uses the refresh grant. Keycloak reuse-detection
 *     (a replayed refresh revokes the whole family) surfaces as a silent-renew error,
 *     which `onSessionEnded` treats as "session over".
 *   - Claims are identity-only (D10): role/tenant never ride the token.
 */
export function createEmgAuth(config: EmgAuthConfig): EmgAuthClient {
    const origin = window.location.origin;

    const userManager = new UserManager({
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        post_logout_redirect_uri: config.postLogoutRedirectUri ?? `${origin}/login`,
        response_type: 'code',
        scope: config.scope ?? 'openid profile email',
        // Refresh-token renewal, not the check-session iframe (see docstring).
        automaticSilentRenew: true,
        monitorSession: false,
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });

    // Synchronous cache backing getAccessTokenSync() — kept current by the events
    // below plus an eager getUser() read on construction so a page load that already
    // has a stored session exposes the token without an initial await.
    let cachedUser: User | null = null;
    userManager.events.addUserLoaded((u) => {
        cachedUser = u;
    });
    userManager.events.addUserUnloaded(() => {
        cachedUser = null;
    });
    void userManager.getUser().then((u) => {
        cachedUser = u;
    });

    async function login(returnTo?: string, options?: EmgAuthLoginOptions): Promise<void> {
        await userManager.signinRedirect({
            state: { returnTo: returnTo ?? '/' },
            // EAA-1765: omit the key entirely unless asked — passing `prompt: undefined`
            // is not the same as not passing it for every issuer.
            ...(options?.prompt ? { prompt: options.prompt } : {}),
        });
    }

    async function handleCallback(): Promise<User> {
        const user = await userManager.signinRedirectCallback();
        cachedUser = user;
        return user;
    }

    async function getAccessToken(): Promise<string | null> {
        const user = await userManager.getUser();
        cachedUser = user;
        if (!user || user.expired) return null;
        return user.access_token;
    }

    function getAccessTokenSync(): string | null {
        if (!cachedUser || cachedUser.expired) return null;
        return cachedUser.access_token;
    }

    async function getClaims(): Promise<EmgAuthClaims | null> {
        const user = await userManager.getUser();
        if (!user || user.expired || !user.profile?.sub) return null;
        // EAA-1730: copy ONLY the identity claims (D10). Even if the issuer added
        // realm_access/resource_access/tenant to the profile, they are dropped here so
        // no consumer can accidentally authorize off the token.
        const { sub, email, name, sid } = user.profile;
        return { sub, email, name, sid };
    }

    // EAA-1762: SINGLE-FLIGHT the refresh grant. Renewal uses ROTATED refresh
    // tokens (see the module docstring), so two concurrent signinSilent() calls
    // replay the same refresh token — which is precisely what Keycloak's reuse
    // detection treats as theft, and it revokes the WHOLE token family. The
    // session then dies on the next renewal, the api client dispatches
    // `auth:logout`, and the app bounces back out to the portal to sign in again:
    // a redirect loop produced by the renewal path itself, not by an expired
    // session. Concurrency is the norm, not the edge case — a page load fires
    // several requests at once and React StrictMode double-invokes effects in
    // dev, so a burst of 401s all reach here together. Every caller in a burst
    // now awaits the SAME grant.
    let inFlightRefresh: Promise<string | null> | null = null;

    async function refreshToken(): Promise<string | null> {
        if (inFlightRefresh) return inFlightRefresh;

        inFlightRefresh = (async () => {
            try {
                const user = await userManager.signinSilent();
                cachedUser = user;
                if (!user || user.expired) return null;
                return user.access_token;
            } catch {
                // Reuse-detection / revoked family / network — treat as no token; the
                // onSessionEnded subscribers (silentRenewError) drive the app cleanup.
                return null;
            } finally {
                // Cleared only once the grant has settled, so the next 401 after a
                // completed refresh starts a fresh one rather than reusing a stale
                // resolved promise.
                inFlightRefresh = null;
            }
        })();

        return inFlightRefresh;
    }

    async function logout(): Promise<void> {
        cachedUser = null;
        await userManager.signoutRedirect();
    }

    function onSessionEnded(cb: () => void): () => void {
        // A session "ends" when Keycloak signs the user out (back-channel/RP), when the
        // access token expires with no renewal, or when a refresh renewal fails
        // (expired/revoked family — the reuse-detection path).
        userManager.events.addUserSignedOut(cb);
        userManager.events.addAccessTokenExpired(cb);
        userManager.events.addSilentRenewError(cb);
        return () => {
            userManager.events.removeUserSignedOut(cb);
            userManager.events.removeAccessTokenExpired(cb);
            userManager.events.removeSilentRenewError(cb);
        };
    }

    function capabilityResolver(capabilities?: Iterable<string> | null): AuthCapability {
        // Fail-closed: with no capabilities resolved (e.g. /me/access not yet loaded),
        // every gated check answers false — a forgotten wire-up never over-grants.
        const granted = new Set<string>(capabilities ?? []);
        return {
            can: (key: string) => granted.has(key),
        };
    }

    return {
        login,
        handleCallback,
        getAccessToken,
        getAccessTokenSync,
        getClaims,
        refreshToken,
        logout,
        onSessionEnded,
        capabilityResolver,
    };
}
